import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import css from './RacingThemeListingReviews.module.css';

const StarIcon = ({ filled }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill={filled ? 'currentColor' : 'none'}
    stroke="currentColor"
    strokeWidth="1.5"
    aria-hidden="true"
  >
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

const Stars = ({ rating }) => (
  <span className={css.stars} aria-label={`${rating} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map(value => (
      <span key={value} className={value <= rating ? css.starOn : css.starOff}>
        <StarIcon filled={value <= rating} />
      </span>
    ))}
  </span>
);

/**
 * Listing reviews summary and cards.
 */
const RacingThemeListingReviews = props => {
  const { average, count, distribution, items, className } = props;
  const maxCount = Math.max(...(distribution || [1]), 1);

  return (
    <section className={classNames(css.root, className)} aria-label="Reviews">
      <div className={css.header}>
        <h2 className={css.title}>Reviews</h2>
        <a className={css.viewAll} href="#reviews">
          View all reviews
        </a>
      </div>

      <div className={css.layout}>
        <div className={css.summary}>
          <div className={css.averageRow}>
            <span className={css.average}>{average}</span>
            <Stars rating={Math.round(average)} />
          </div>
          <p className={css.count}>{count} reviews</p>
          <ul className={css.bars}>
            {[5, 4, 3, 2, 1].map(star => {
              const value = distribution[star - 1] || 0;
              const width = `${Math.round((value / maxCount) * 100)}%`;
              return (
                <li key={star} className={css.barRow}>
                  <span className={css.barLabel}>{star}</span>
                  <span className={css.barTrack}>
                    <span className={css.barFill} style={{ width }} />
                  </span>
                </li>
              );
            })}
          </ul>
        </div>

        <ul className={css.cards}>
          {items.map(item => (
            <li key={item.id} className={css.card}>
              <div className={css.cardTop}>
                <span className={css.cardName}>{item.name}</span>
                {item.verified ? <span className={css.verified}>Verified Buyer</span> : null}
              </div>
              <Stars rating={item.rating} />
              <p className={css.cardBody}>{item.body}</p>
              <p className={css.cardTime}>{item.timeAgo}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

RacingThemeListingReviews.defaultProps = {
  className: null,
  distribution: [0, 0, 0, 0, 0],
  items: [],
};

RacingThemeListingReviews.propTypes = {
  className: PropTypes.string,
  average: PropTypes.number.isRequired,
  count: PropTypes.number.isRequired,
  distribution: PropTypes.arrayOf(PropTypes.number),
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      verified: PropTypes.bool,
      rating: PropTypes.number.isRequired,
      body: PropTypes.string.isRequired,
      timeAgo: PropTypes.string.isRequired,
    })
  ),
};

export default RacingThemeListingReviews;
